import { useRef, useState } from "react";
import { Link } from "react-router";
import { Avatar } from "../../ui/Avatar";
import { cx } from "../../ui/cx";
import { Icon } from "../../ui/Icon";
import { Menu, MenuDivider, MenuItem, MenuLink } from "../../ui/Menu";
import { Popover, useDisclosure } from "../../ui/Popover";
import { brand, Logo, LogoMark } from "../brand";
import { isWorkspaceAdmin, useLogout, useMe, useSwitchWorkspace, useWorkspace } from "../session";
import { s } from "../strings";
import styles from "./TopBar.module.css";

export interface TopBarProps {
  onOpenNavigation?: () => void;
}

/** Fixed 60px bar. No Upgrade button and no plan banners: everything is free in the clone. */
export function TopBar({ onOpenNavigation }: TopBarProps) {
  const workspace = useWorkspace();
  return (
    <header className={styles.bar}>
      {onOpenNavigation && (
        <button type="button" className={styles.hamburger} aria-label={s.openNavigation} onClick={onOpenNavigation}>
          <Icon name="menu" />
        </button>
      )}
      <WorkspaceSwitcher />
      <Link to="/" className={styles.logo} aria-label={brand.productName}>
        <Logo size={28} />
      </Link>
      {workspace && (
        <p className={styles.workspaceName} title={workspace.name}>
          {workspace.name}
        </p>
      )}
      <WorkspaceOptions />
      <div className={styles.spacer} />
      <NotificationsBell />
      <HelpMenu />
      <ProfileMenu />
    </header>
  );
}

function WorkspaceSwitcher() {
  const me = useMe();
  const current = useWorkspace();
  const sw = useSwitchWorkspace();
  const { open, toggle, close, anchorRef } = useDisclosure();
  return (
    <>
      <button
        ref={anchorRef}
        type="button"
        className={styles.switcherButton}
        aria-label={s.switcher}
        aria-expanded={open}
        onClick={toggle}
      >
        <Icon name="grid" size={22} strokeWidth={1.5} />
      </button>
      <Popover
        open={open}
        onClose={close}
        anchorRef={anchorRef}
        placement="bottom-start"
        offset={10}
        surface="bare"
        className={styles.switcherPanel}
        role="dialog"
        aria-label={s.switcher}
      >
        <div className={styles.product}>
          <div className={styles.productHeader}>
            <LogoMark size={32} />
            <div className={styles.productText}>
              <div className={styles.productName}>{brand.productName}</div>
              <div className={styles.productTagline}>{brand.tagline}</div>
            </div>
            <Icon name="chevron-up" size={18} className={styles.productChevron} />
          </div>
          <div className={styles.workspaceList}>
            {me.workspaces.map((w) => (
              <button
                key={w.id}
                type="button"
                className={cx(styles.workspaceRow, w.id === current?.id && styles.workspaceRowActive)}
                aria-current={w.id === current?.id || undefined}
                disabled={sw.isPending}
                onClick={() => {
                  close();
                  if (w.id !== current?.id) sw.mutate(w.id);
                }}
              >
                {w.name}
              </button>
            ))}
          </div>
        </div>
        <Link to="/workspaces?create=1" className={styles.createRow} onClick={close}>
          <Icon name="plus-circle" size={18} />
          {s.switcherCreate}
        </Link>
      </Popover>
    </>
  );
}

function WorkspaceOptions() {
  const workspace = useWorkspace();
  const { open, toggle, close, anchorRef } = useDisclosure();
  return (
    <>
      <button
        ref={anchorRef}
        type="button"
        className={styles.optionsToggle}
        aria-label={s.workspaceOptions}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={toggle}
      >
        <Icon name="more-horizontal" />
      </button>
      <Menu
        open={open}
        onClose={close}
        anchorRef={anchorRef}
        placement="bottom-start"
        offset={2}
        width={203}
        rowHeight={43}
        aria-label={s.workspaceOptions}
      >
        {workspace && isWorkspaceAdmin(workspace) && (
          <MenuLink to={`/workspaces/${workspace.id}/settings#general`}>{s.workspaceSettings}</MenuLink>
        )}
        <MenuLink to="/workspaces">{s.manageWorkspaces}</MenuLink>
      </Menu>
    </>
  );
}

function NotificationsBell() {
  const { open, toggle, close, anchorRef } = useDisclosure();
  return (
    <>
      <button
        ref={anchorRef}
        type="button"
        className={styles.iconCell}
        aria-label={s.notifications}
        aria-expanded={open}
        onClick={toggle}
      >
        <Icon name="bell" size={22} strokeWidth={1.4} />
      </button>
      <Popover
        open={open}
        onClose={close}
        anchorRef={anchorRef}
        placement="bottom-end"
        offset={1}
        className={styles.notifications}
        role="dialog"
        aria-label={s.notifications}
      >
        <div className={styles.notificationsHeader}>
          <span className={styles.notificationsTitle}>{s.notifications}</span>
          <button type="button" className={styles.clearAll}>
            {s.clearAll}
          </button>
        </div>
        <hr className={styles.rule} />
        <span className={styles.emptyRow}>{s.noNotifications}</span>
      </Popover>
    </>
  );
}

function HelpMenu() {
  const { open, toggle, close, anchorRef } = useDisclosure();
  return (
    <>
      <button
        ref={anchorRef}
        type="button"
        className={styles.iconCell}
        aria-label={s.help}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={toggle}
      >
        <Icon name="help" size={22} strokeWidth={1.4} />
      </button>
      <Menu open={open} onClose={close} anchorRef={anchorRef} offset={1} rowHeight={43} aria-label={s.help}>
        <MenuLink to={brand.helpUrl} external>
          {s.helpCenter}
        </MenuLink>
        <MenuLink to={brand.whatsNewUrl} external>
          {s.whatsNew}
        </MenuLink>
      </Menu>
    </>
  );
}

function ProfileMenu() {
  const me = useMe();
  const logout = useLogout();
  const anchorRef = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);
  const user = me.user;
  return (
    <>
      <button
        ref={anchorRef}
        type="button"
        className={styles.avatarButton}
        aria-label={s.profileMenu}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        <Avatar name={user.name || user.email} colorSeed={user.id} src={user.profilePicture} size={32} />
      </button>
      <Menu
        open={open}
        onClose={() => setOpen(false)}
        anchorRef={anchorRef}
        placement="bottom-end"
        offset={5}
        width={300}
        rowHeight={43}
        radius="profile"
        aria-label={s.profileMenu}
      >
        <div className={styles.profileHeader}>
          <Avatar name={user.name || user.email} colorSeed={user.id} src={user.profilePicture} size={72} />
          <div className={styles.profileName}>{user.name}</div>
          <div className={styles.profileEmail}>{user.email}</div>
          <Link to="/user/settings" className={styles.manageAccount} onClick={() => setOpen(false)}>
            {s.manageAccount}
          </Link>
        </div>
        <MenuDivider />
        <MenuLink to="/user/settings">{s.myProfile}</MenuLink>
        <MenuLink to="/user/preferences#general">{s.preferences}</MenuLink>
        <MenuItem onSelect={() => logout.mutate()}>{s.logOut}</MenuItem>
      </Menu>
    </>
  );
}
